import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import BsModal from 'react-bootstrap/Modal';
import { FaFilePdf } from 'react-icons/fa';
import statementService from '../../services/statement.service';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError, showCurrency } from '../../utils';
import { Button, Checkbox } from '../EntityList';

const dueTotal = (due) => (due.amount || 0) + (due.surcharge || 0);
const isOpen = (due) => (due.unpaidAmount ?? (due.paid ? 0 : dueTotal(due))) > 0;

/**
 * Picks the dues of one debt or credit and downloads their "Estado de cuenta"
 * PDF (SHOW_CLIENT_STATEMENT) to send to the client/supplier. Starts with the
 * dues still owed selected.
 */
function StatementModal({ show, kind, record, dues, onClose }) {
	const { t } = useLabels();
	const [selected, setSelected] = useState(() => new Set());
	const [busy, setBusy] = useState(false);

	const sorted = useMemo(
		() => [...(dues || [])].sort((a, b) => moment.utc(a.expiration || a.date).diff(moment.utc(b.expiration || b.date))),
		[dues],
	);

	useEffect(() => {
		if (show) setSelected(new Set(sorted.filter(isOpen).map((d) => d._id)));
	}, [show, sorted]);

	const toggle = (id) => setSelected((prev) => {
		const next = new Set(prev);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		return next;
	});

	const currency = showCurrency(record?.currency) || '';
	const money = (v) => `${currency} ${Number(v || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

	async function download() {
		setBusy(true);
		try {
			const ids = sorted.filter((d) => selected.has(d._id)).map((d) => d._id);
			const response = await statementService.getPdf(kind, record._id, ids);
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
			const link = document.createElement('a');
			link.href = url;
			const who = (record.owner?.name || record.customId || record._id || '').replace(/[^\w.-]+/g, '_');
			link.download = `Estado_de_cuenta_${who}_${moment().format('DD-MM-YYYY')}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			onClose();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<BsModal show={show} onHide={busy ? undefined : onClose} centered size='lg'>
			<BsModal.Header closeButton={!busy} className='tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'>
				<div>
					<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:text-ink-0'>{t('statement.title')}</BsModal.Title>
					<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
						{record?.owner?.name}{record?.customId ? ` · ${record.customId}` : ''}
					</p>
				</div>
			</BsModal.Header>
			<BsModal.Body className='tw:px-[22px] tw:py-4'>
				<div className='tw:mb-3 tw:flex tw:flex-wrap tw:gap-2'>
					<Button onClick={() => setSelected(new Set(sorted.filter(isOpen).map((d) => d._id)))}>Pendientes</Button>
					<Button onClick={() => setSelected(new Set(sorted.map((d) => d._id)))}>Todas</Button>
					<Button onClick={() => setSelected(new Set())}>Ninguna</Button>
				</div>
				{sorted.length === 0 ? (
					<p className='tw:text-[13px] tw:text-ink-3'>No hay cuotas generadas.</p>
				) : (
					<div className='tw:max-h-[50vh] tw:overflow-y-auto tw:rounded-[7px] tw:border tw:border-border'>
						<table className='tw:w-full tw:text-[13px]'>
							<thead className='tw:bg-surface-2 tw:text-[11px] tw:uppercase tw:tracking-[0.05em] tw:text-ink-3'>
								<tr>
									<th className='tw:px-3 tw:py-2' />
									<th className='tw:px-3 tw:py-2 tw:text-left'>{t('statement.col.due')}</th>
									<th className='tw:px-3 tw:py-2 tw:text-left'>{t('statement.col.expiration')}</th>
									<th className='tw:px-3 tw:py-2 tw:text-right'>{t('statement.col.amount')}</th>
									<th className='tw:px-3 tw:py-2 tw:text-right'>{t('statement.col.pending')}</th>
								</tr>
							</thead>
							<tbody>
								{sorted.map((due) => (
									<tr key={due._id} className='tw:border-t tw:border-border'>
										<td className='tw:px-3 tw:py-2'>
											<Checkbox id={`statement-due-${due._id}`} checked={selected.has(due._id)} onChange={() => toggle(due._id)} />
										</td>
										<td className='tw:px-3 tw:py-2 tw:font-mono tw:text-[12px]'>{due.customId}</td>
										<td className='tw:px-3 tw:py-2'>{due.expiration ? moment.utc(due.expiration).format('DD/MM/YYYY') : '—'}</td>
										<td className='tw:px-3 tw:py-2 tw:text-right tw:tabular-nums'>{money(dueTotal(due))}</td>
										<td className='tw:px-3 tw:py-2 tw:text-right tw:tabular-nums'>{money(due.unpaidAmount ?? 0)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</BsModal.Body>
			<BsModal.Footer className='tw:border-t tw:border-border tw:px-[22px] tw:py-3'>
				<Button onClick={onClose} disabled={busy}>{t('action.cancel')}</Button>
				<Button variant='primary' onClick={download} disabled={busy || selected.size === 0}>
					<FaFilePdf className='tw:mr-1.5' />
					{busy ? 'Generando…' : `Descargar PDF (${selected.size})`}
				</Button>
			</BsModal.Footer>
		</BsModal>
	);
}

StatementModal.propTypes = {
	show: PropTypes.bool.isRequired,
	kind: PropTypes.oneOf(['debt', 'credit']).isRequired,
	record: PropTypes.object,
	dues: PropTypes.array,
	onClose: PropTypes.func.isRequired,
};

export default StatementModal;
